import { CheckCircle2, ChevronDown, Copy, Expand, Loader, RefreshCw, Sparkles, XCircle } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { copyToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";

import { type ClaimWithRelations, useClaim } from "../../api/use-claim";
import { type AssessmentMetadata, useGenerateClaimAssessment } from "../../api/use-claim-insights";
import { getAiEligibilityVerdict } from "../../lib/ai-verdict";
import type { AiIncidentData } from "../../lib/incident-delta";
import { Markdown } from "../../lib/markdown";
import { isPartnerFeedOffice } from "../../lib/partner-feed";
import { SelectAbandonType } from "../select-abandon-type";
import { AbandonClaimButton, getDefaultAbandonTypeId } from "./abandon-claim-button";
import { NegativeCriteria } from "./ai-insights-panel";
import { InitIncidentWorkflow } from "./init-incident-workflow";
import { InsightFeedback } from "./insight-feedback";

/**
 * "Étude de dossier" card: AI verdict as title, assessment summary,
 * (re)generate + copy actions, a button opening the full incident view in a
 * modal, and — when the verdict is "non éligible" — the abandon flow with the
 * most likely abandon type preselected. Port of EligibilityInsight.vue.
 */

/** Mirrors ClaimStore.isManualClaim (Vue): manual unless created by the API service accounts. */
function isManualClaim(claim: ClaimWithRelations | undefined): boolean {
  const createdEvent = claim?.claimEvents?.find(
    (event) => event.eventType?.type === "CLAIM_VALIDATION.CLAIM_CREATED",
  );
  const email = createdEvent?.admin?.email;
  return email !== "api@upclaim.eu" && email !== "romain@upclaim.eu";
}

interface EligibilityInsightProps {
  claimId: string;
}

export function EligibilityInsight({ claimId }: EligibilityInsightProps) {
  const { data: claim } = useClaim(claimId);
  const generateAssessment = useGenerateClaimAssessment(claimId);
  const [showIncidentModal, setShowIncidentModal] = useState(false);

  const assessment = claim?.insights?.find((insight) => insight?.type?.type === "ASSESSMENT") ?? null;
  // Legacy "V3" claims have no ASSESSMENT — they carry an INCIDENT insight instead.
  const v3Incident = claim?.insights?.find((insight) => insight?.type?.type === "INCIDENT") ?? null;
  const contradictions = claim?.insights?.find((insight) => insight?.type?.type === "CONTRADICTIONS") ?? null;
  const cxInsight = claim?.insights?.find((insight) => insight?.type?.type === "CX") ?? null;

  // Recap shown in the card: the assessment summary for V042026, or — for legacy
  // V3 claims — the incident notes (the same "Résumé de l'incident" the layout shows).
  const assessmentMetadata = assessment?.metadata as AssessmentMetadata | null | undefined;
  const v3Metadata = v3Incident?.metadata as AiIncidentData | null | undefined;
  const summary = assessmentMetadata?.context?.summary ?? v3Metadata?.incident?.notes ?? null;
  const hasStudy = !!assessment;
  const canView = !!assessment || !!v3Incident;

  const isEligible = getAiEligibilityVerdict(claim);

  const manualClaim = isManualClaim(claim);
  const generating = generateAssessment.isPending;

  // Abandon only makes sense while the claim still awaits its eligibility verification.
  const awaitingEligibilityCheck = claim?.claimState?.type === "CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK";

  // Partner-feed claims (Digitrips) have no Amadeus PNR: the AI study can never be
  // generated — hide the generate action instead of a dead button.
  const partnerFeedClaim = isPartnerFeedOffice(
    claim?.legalSpecification?.impactedSegment?.reservation?.travelAgencyOffice?.amadeusOfficeId,
  );

  const GenerateIcon = hasStudy ? RefreshCw : Sparkles;

  // Non éligible: abandoning is the expected next step, so it takes the (re)generation's slot.
  const abandonIsPrimary = isEligible === false;

  function copyRawInsights() {
    void copyToClipboard(
      JSON.stringify(
        {
          assessment: assessment?.metadata ?? null,
          contradictions: contradictions?.metadata ?? null,
          incident: v3Incident?.metadata ?? null,
          cx: cxInsight?.metadata ?? null,
        },
        null,
        2,
      ),
    );
  }

  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-6 ring-2 ring-purple-100">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <Sparkles className="h-8 w-8 text-purple-600" />
        </div>
        {/* min-w-0: wide PNR source lines must scroll inside the card, not stretch it */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Title: the AI verdict when available, "Étude de dossier" otherwise */}
          <div className="flex items-center gap-2">
            {isEligible === undefined ? (
              <h3 className="text-xl font-bold tracking-tight text-zinc-900">Étude de dossier</h3>
            ) : isEligible ? (
              <h3 className="flex items-center gap-2 text-xl font-bold tracking-tight text-emerald-700">
                <CheckCircle2 className="h-6 w-6" />
                Verdict : Éligible
              </h3>
            ) : (
              <h3 className="flex items-center gap-2 text-xl font-bold tracking-tight text-rose-700">
                <XCircle className="h-6 w-6" />
                Verdict : Non éligible
              </h3>
            )}
            {!hasStudy && v3Incident ? (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Analyse héritée (V3)
              </span>
            ) : null}
            {canView ? (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-zinc-500"
                title="Copier la raw JSON des insights"
                onClick={copyRawInsights}
              >
                <Copy />
                Copier l'insight
              </Button>
            ) : null}
          </div>

          {/* Recap (text only): assessment summary for V042026, incident notes for V3 */}
          {summary ? (
            <Markdown text={summary} className="max-w-none text-sm text-gray-700" />
          ) : (
            <p className="text-sm italic text-gray-500">
              {partnerFeedClaim
                ? "Étude IA indisponible pour les dossiers partenaires (pas de PNR Amadeus)."
                : "Aucune étude de dossier générée pour le moment."}
            </p>
          )}

          {/* Actions — no generate action for partner-feed claims: it can never succeed. */}
          {canView || !partnerFeedClaim ? (
            <div className="flex gap-2">
              {canView ? (
                <Button className="flex-1" onClick={() => setShowIncidentModal(true)}>
                  <Expand />
                  {hasStudy ? "Voir l'étude" : "Voir l'incident (V3)"}
                </Button>
              ) : null}
              {abandonIsPrimary ? (
                <AbandonClaimButton claimId={claimId} className="flex-1" />
              ) : !partnerFeedClaim ? (
                <span
                  title={
                    manualClaim ? "Étude IA indisponible pour les dossiers créés manuellement" : undefined
                  }
                  className={cn(canView ? "flex-1" : "w-full")}
                >
                  {/* Engine choice: same study either way, so it's one action with two
                      variants rather than two buttons. */}
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      disabled={generating || manualClaim}
                      render={<Button variant={canView ? "outline" : "default"} className="w-full" />}
                    >
                      {generating ? <Loader className="animate-spin" /> : <GenerateIcon />}
                      {hasStudy ? "Régénérer l'étude" : "Générer l'étude"}
                      <ChevronDown />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => generateAssessment.mutate("claude")}>
                        Avec Claude
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => generateAssessment.mutate("gemini")}>
                        Avec Gemini
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Non éligible → only the negative criteria (the reasons), above the abandon flow */}
          {isEligible === false ? <NegativeCriteria assessment={assessmentMetadata ?? null} /> : null}

          {/* Jurist feedback on the study — the written note is what feeds prompt work back. */}
          {hasStudy ? (
            <InsightFeedback
              claimId={claimId}
              insight={assessment}
              question="Cette étude est-elle correcte ?"
              className="!mt-4"
            />
          ) : null}

          {/* Abandon flow while the claim awaits its eligibility verification. Non éligible: the
              red « Abandonner » action above opens it in a modal; otherwise it stays inline,
              subdued gray and set apart by a divider. */}
          {awaitingEligibilityCheck && !abandonIsPrimary ? (
            <>
              <hr className="!mt-6 border-purple-200/70" />
              <div className="!mt-6 rounded-lg border border-gray-200 bg-gray-50/50 py-4">
                <h4 className="mb-2 px-6 text-sm font-semibold text-gray-600">Abandonner le dossier :</h4>
                <SelectAbandonType claimId={claimId} defaultAbandonTypeId={getDefaultAbandonTypeId(claim)} />
              </div>
            </>
          ) : null}
        </div>
      </div>

      <Dialog open={showIncidentModal} onOpenChange={setShowIncidentModal}>
        <DialogContent className="max-h-[90vh] sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Étude de dossier</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            {showIncidentModal && canView ? (
              <InitIncidentWorkflow
                claimId={claimId}
                onIncidentConfirmed={() => setShowIncidentModal(false)}
              />
            ) : null}
          </DialogPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
